import type { ReactNode } from 'react';

import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/atoms';

type ReasoningTooltipProps = {
  reasoning?: string | null;
  /**
   * Rendered above the reasoning so the tooltip names what it is explaining — the Signal
   * chip or the Risk Type label. Passed in already styled, because the two callers look
   * different (a level-coloured tag pill vs. an icon + risk-type title).
   */
  title?: ReactNode;
  children: ReactNode;
};

/**
 * BAL-8159: wraps a Risk Type row / Signal chip so hovering it reveals the model's
 * concise `reasoning`, headed by the item it belongs to. When there is no reasoning — an
 * older report that predates the field — it renders `children` unchanged, with no hover
 * affordance (per the "empty = no tooltip" product decision).
 */
export const ReasoningTooltip = ({ reasoning, title, children }: ReasoningTooltipProps) => {
  const text = reasoning?.trim();

  if (!text) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        {/* Background / shade / stroke deliberately mirror the DF table's Risks-column tooltip
            (`RiskIndicatorTooltip` → `ContentTooltip`): `bg-background`, a 1px border, and the
            same soft shadow. Scoped here rather than on the shared `Tooltip` atom, so Risk
            Types / Signals restyle without touching every other tooltip. */}
        <TooltipContent
          side="top"
          className="bg-background max-w-xs rounded-[6px] border px-4 py-3 shadow-[0px_4px_4px_rgba(0,0,0,0.05)]"
        >
          {title && <div className="mb-2 flex">{title}</div>}
          <p className="text-sm leading-relaxed text-black">{text}</p>
          <TooltipArrow className="fill-background" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
